export interface EndpointCandidate {
  url: string;
  priority?: number;
}

export interface ResolvedEndpoint {
  url: string;
  latencyMs: number;
}

type FetchLike = typeof fetch;

const memoryCache = new Map<string, ResolvedEndpoint>();

const normalizeRoot = (url: string): string => url.replace(/\/+$/, '');

const defaultProbePath = '/health';

/**
 * Computes the probe URL for a candidate base URL.
 * Health endpoint lives outside the versioned API prefix (/api/vX),
 * so strip the trailing version segment when present.
 */
export function computeHealthProbeUrl(candidateUrl: string, probePath: string = defaultProbePath): string {
  const normalized = normalizeRoot(candidateUrl);
  // remove a trailing /api/v<number> segment if present (with optional slash)
  const stripped = normalized.replace(/\/api\/v\d+$/, '');
  const probeBase = stripped || normalized;
  return `${probeBase}${probePath}`;
}

async function probeEndpoint(
  candidate: EndpointCandidate,
  fetchImpl: FetchLike,
  timeoutMs: number,
): Promise<ResolvedEndpoint> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const start = Date.now();

  try {
    const root = normalizeRoot(candidate.url);
    const probeUrl = computeHealthProbeUrl(root);
    const response = await fetchImpl(probeUrl, {
      method: 'GET',
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`Probe failed with status ${response.status}`);
    }
    let ok = false;
    try {
      const body = await response.json();
      ok = body?.status === 'ok';
    } catch {
      // If JSON parse fails, consider it a failed probe
      ok = false;
    }
    if (!ok) {
      throw new Error('Probe did not return status ok');
    }
    return { url: root, latencyMs: Date.now() - start };
  } finally {
    clearTimeout(timer);
  }
}

export async function resolveEndpoint(
  candidates: EndpointCandidate[],
  options: {
    fetchImpl?: FetchLike;
    timeoutMs?: number;
    cache?: boolean;
    cacheKey?: string;
  } = {},
): Promise<ResolvedEndpoint> {
  if (!candidates || candidates.length === 0) {
    throw new Error('No endpoint candidates provided');
  }

  const fetchImpl = options.fetchImpl ?? fetch;
  if (typeof fetchImpl !== 'function') {
    throw new Error('Fetch implementation is required to resolve endpoints');
  }

  const timeoutMs = options.timeoutMs ?? 1000;
  const sorted = [...candidates].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  const cacheKey = options.cacheKey ?? JSON.stringify(sorted.map(c => ({ url: normalizeRoot(c.url), priority: c.priority ?? 0 })));

  if (options.cache !== false && cacheKey && memoryCache.has(cacheKey)) {
    return memoryCache.get(cacheKey)!;
  }

  let lastError: Error | null = null;
  for (const candidate of sorted) {
    try {
      const resolved = await probeEndpoint(candidate, fetchImpl, timeoutMs);
      if (options.cache !== false && cacheKey) {
        memoryCache.set(cacheKey, resolved);
      }
      return resolved;
    } catch (err: any) {
      lastError = err instanceof Error ? err : new Error(String(err));
    }
  }

  throw lastError ?? new Error('Failed to resolve any endpoint candidate');
}

export function clearResolvedEndpointCache(cacheKey?: string): void {
  if (!cacheKey) {
    memoryCache.clear();
    return;
  }
  memoryCache.delete(cacheKey);
}
